/**
 * The CRM's stacking order, named once.
 *
 * The shell grew its overlay layers by hand (sidebar rail, sticky app bar,
 * call workspace + softphone, modals, a modal-over-modal tier, the
 * leave-the-CRM confirm) and the only record of which sits above which was
 * the arbitrary numbers themselves. These tokens name every level the shell
 * relies on so new kit surfaces stop guessing.
 *
 * Binding semantics:
 * - Kit components use these tokens. Existing arbitrary z values are migrated
 *   onto them ONLY where the swap is order-preserving (same effective level) —
 *   a restyle must never reshuffle what covers what.
 * - The modal-over-modal tier (D-2) is RESOLVED, not ad hoc: `modal` (50) is
 *   the standard level; `modalRaised` (70) is for a surface that must cover an
 *   already-open standard modal; `modalCeiling` (80) is the one tier above
 *   that, reserved for a surface that can itself open from inside a raised
 *   modal. The old `z-[65]`/`z-[60]` one-off sites were folded into `modal` or
 *   `modalRaised` per their actual reachable stacking — never carried forward
 *   as a fourth, unnamed level.
 *
 * `Z` holds the Tailwind classes (what components render); `Z_INDEX` holds the
 * same levels as numbers for the rare inline-style or JS comparison. Keep them
 * in step.
 */

/** Tailwind z-index classes, one per shell level. */
export const Z = {
  /** Sidebar icon rail / in-page sticky columns. */
  rail: 'z-10',
  /** In-page dropdowns, row-action menus, and hover popovers. Above the
   *  rail's sticky in-page columns, below the app bar. */
  menu: 'z-20',
  /** A `menu`-tier popover that goes full-bleed sheet below `sm:` and must
   *  clear page chrome there (the AddFilterMenu pattern). */
  menuSheet: 'z-20 max-sm:z-50',
  /** Sticky app bar and the mobile bottom tab bar. */
  header: 'z-30',
  /** Full-screen call workspace, softphone bar, docked toasts. */
  overlay: 'z-40',
  /** Standard modals and drawers. */
  modal: 'z-50',
  /** The softphone/call-dock surfaces. Deliberately the SAME numeric level as
   *  `modal` — they aren't modals competing for the modal tier, they win by
   *  DOM order instead (mounted after routes). D-2's OverlayHost renders its
   *  call-dock container at this tier. */
  callDock: 'z-50',
  /** A modal, drawer, or confirm that must cover an already-open z-50 modal. */
  modalRaised: 'z-[70]',
  /** The one tier above `modalRaised`; today only FormResponseModal, which
   *  can open from inside raised modals. */
  modalCeiling: 'z-[80]',
  /** Confirm/alert/prompt dialogs — above EVERYTHING, because anything may
   *  raise one, including a raised modal. Live case: PrescreeningSummaryModal
   *  at `modalRaised` (70) confirms before sending PHI to CRIO — that confirm
   *  must not mount invisibly behind the modal that raised it. 90 clears the
   *  whole tier, ceiling included. */
  confirm: 'z-[90]',
} as const;

/** Numeric twins of `Z`, for inline styles and comparisons. */
export const Z_INDEX = {
  rail: 10,
  menu: 20,
  /** Base tier only — `menuSheet`'s `max-sm:z-50` sheet override has no
   *  single numeric twin. Comparisons against this constant should treat it
   *  as `menu` (20); the sheet break is a Tailwind-only concern. */
  menuSheet: 20,
  header: 30,
  overlay: 40,
  modal: 50,
  callDock: 50,
  modalRaised: 70,
  modalCeiling: 80,
  confirm: 90,
} as const;
